import { useAuth } from "react-oidc-context";

export function useUserRole() {
  const auth = useAuth();

  if (!auth.isAuthenticated || !auth.user) {
    return {
      isAuthenticated: false,
      groups: [],
      roles: [],
      hasRole: () => false,
      hasAnyRole: () => false,
      isAdmin: false,
      isMarketing: false,
      isPhotographer: false,
      isViewer: false,
      user: null
    };
  }

  // Extract roles from Auth0 JWT token claims
  // Using custom namespace as primary source (configured in Auth0 Login Action)
  const groups = auth.user?.profile?.['https://srfcmedia.shamrockrovers.ie/roles'] || [];
  const roles = groups; // Alias for groups

  // Role hierarchy for permission checks
  const ROLE_HIERARCHY = {
    admin: 4,
    marketing: 3,
    photographer: 2,
    viewer: 1
  };

  // Get highest role from user's groups
  function getHighestRole(userGroups) {
    if (!userGroups || userGroups.length === 0) {
      return null;
    }
    
    let highestRole = null;
    let highestLevel = 0;
    
    for (const group of userGroups) {
      const level = ROLE_HIERARCHY[group];
      if (level && level > highestLevel) {
        highestLevel = level;
        highestRole = group;
      }
    }
    
    return highestRole;
  }

  // Check if user has required role or higher
  function hasRole(requiredRole) {
    if (!requiredRole) return true;
    if (!groups || groups.length === 0) return false;
    
    const userLevel = getHighestRole(groups);
    const requiredLevel = ROLE_HIERARCHY[requiredRole];
    const currentLevel = ROLE_HIERARCHY[userLevel];
    
    return currentLevel >= requiredLevel;
  }

  // Check if user has any of the specified roles
  function hasAnyRole(requiredRoles) {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    if (!groups || groups.length === 0) return false;
    
    return requiredRoles.some(role => groups.includes(role));
  }

  return {
    isAuthenticated: true,
    groups,
    roles,
    hasRole,
    hasAnyRole,
    isAdmin: groups.includes('admin'),
    isMarketing: groups.includes('marketing'),
    isPhotographer: groups.includes('photographer'),
    isViewer: groups.includes('viewer'),
    highestRole: getHighestRole(groups),
    user: {
      id: auth.user.profile.sub,
      email: auth.user.profile.email,
      username: auth.user.profile.nickname || auth.user.profile.preferred_username || auth.user.profile.email,
      groups,
      roles
    }
  };
}